/**
 * Buro42 - Main Application
 */

import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { authService } from './services/auth.service';
import { AuthProvider } from './context/AuthContext';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Login from './pages/auth/Login';
import Dashboard from './pages/Dashboard';
import RelationsList from './pages/relations/RelationsList';
import RelationForm from './pages/relations/RelationForm';
import RelationDetail from './pages/relations/RelationDetail';
import WerknemersList from './pages/werknemers/WerknemersList';
import WerknemerForm from './pages/werknemers/WerknemerForm';
import BootModellenList from './pages/boot-modellen/BootModellenList';
import BootModelForm from './pages/boot-modellen/BootModelForm';
import BotenList from './pages/boten/BotenList';
import BotenForm from './pages/boten/BotenForm';
import UsersList from './pages/settings/UsersList';
import UsersForm from './pages/settings/UsersForm';
import Bedrijfsgegevens from './pages/settings/Bedrijfsgegevens';
import EmailInstellingen from './pages/settings/EmailInstellingen';
import AlgemeneInstellingen from './pages/settings/AlgemeneInstellingen';
import RollenPermissies from './pages/settings/RollenPermissies';
import DatabaseExport from './pages/settings/DatabaseExport';
import TodoList from './pages/settings/TodoList';
import KlantPortaal from './pages/portaal/KlantPortaal';
import OffertePortaalView from './pages/portaal/OffertePortaalView';
import OnderdelenList from './pages/onderdelen/OnderdelenList';
import OnderdeelForm from './pages/onderdelen/OnderdeelForm';
import OnderdeelDocumentenList from './pages/onderdelen/OnderdeelDocumentenList';
import OnderdeelDocumentForm from './pages/onderdelen/OnderdeelDocumentForm';
import MotorOnderdelenView from './pages/motor-onderdelen/MotorOnderdelenView';
import VoorraadList from './pages/voorraad/VoorraadList';
import VoorraadGroepDetail from './pages/voorraad/VoorraadGroepDetail';
import SubgroepForm from './pages/voorraad/SubgroepForm';
import ItemOnderdelenBeheer from './pages/voorraad/ItemOnderdelenBeheer';
import OffertesList from './pages/offertes/OffertesList';
import OfferteForm from './pages/offertes/OfferteForm';
import OfferteDetail from './pages/offertes/OfferteDetail';
import WerkordersList from './pages/werkorders/WerkordersList';
import WerkorderDetail from './pages/werkorders/WerkorderDetail';
import WerkorderForm from './pages/werkorders/WerkorderForm';
import UrenRegistratie from './pages/urenregistratie/UrenRegistratie';
import KilometerRegistratie from './pages/kilometerregistratie/KilometerRegistratie';
import BestellingenList from './pages/bestellingen/BestellingenList';
import BestellingForm from './pages/bestellingen/BestellingForm';
import BestellingenHistorie from './pages/bestellingen/BestellingenHistorie';
import FacturenList from './pages/facturen/FacturenList';
import FactuurForm from './pages/facturen/FactuurForm';
import FactuurDetail from './pages/facturen/FactuurDetail';
import BoekhoudingDashboard from './pages/boekhouding/Dashboard';
import BoekhoudingInstellingen from './pages/boekhouding/Instellingen';
import BoekingenList from './pages/boekhouding/BoekingenList';
import BoekingForm from './pages/boekhouding/BoekingForm';
import BoekingDetail from './pages/boekhouding/BoekingDetail';
import BTWOverzicht from './pages/boekhouding/BTWOverzicht';
import Exploitatie from './pages/boekhouding/Exploitatie';
import Balans from './pages/boekhouding/Balans';
import Afschrijvingen from './pages/boekhouding/Afschrijvingen';
import BeginbalansPage from './pages/boekhouding/BeginbalansPage';
import Kapitaal from './pages/boekhouding/Kapitaal';
import Help from './pages/Help';
import BoekhoudingHelp from './pages/boekhouding/Help';
import Leningen from './pages/boekhouding/Leningen';
import DebiteurenCrediteuren from './pages/boekhouding/DebiteurenCrediteuren';
import Jaarrekening from './pages/boekhouding/Jaarrekening';
import Relaties from './pages/boekhouding/Relaties';
import RelatieForm from './pages/boekhouding/RelatieForm';
import FactuurScan from './pages/boekhouding/FactuurScan';

import './App.css';

const AppContent = () => {
  const isAuthenticated = authService.isAuthenticated();

  return (
    <div>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <Login />
          }
        />

        {/* Klantenportaal - Publiek toegankelijk met token */}
        <Route
          path="/portaal/:token"
          element={<KlantPortaal />}
        />
        <Route
          path="/portaal/:token/offerte/:offerteId"
          element={<OffertePortaalView />}
        />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout key={authService.isAuthenticated() ? 'authenticated' : 'not-authenticated'}>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/relations"
          element={
            <ProtectedRoute requiredPermission="relaties_bekijken">
              <Layout>
                <RelationsList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/relations/new"
          element={
            <ProtectedRoute requiredPermission="relaties_aanmaken">
              <Layout>
                <RelationForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/relations/:id"
          element={
            <ProtectedRoute requiredPermission="relaties_bekijken">
              <Layout>
                <RelationDetailWrapper />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Werknemers Routes */}
        <Route
          path="/werknemers"
          element={
            <ProtectedRoute>
              <Layout>
                <WerknemersList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/werknemers/new"
          element={
            <ProtectedRoute>
              <Layout>
                <WerknemerForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/werknemers/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <WerknemerFormWrapper />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Boot Modellen Routes */}
        <Route
          path="/boot-modellen"
          element={
            <ProtectedRoute >
              <Layout>
                <BootModellenList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/boot-modellen/new"
          element={
            <ProtectedRoute >
              <Layout>
                <BootModelForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/boot-modellen/:id"
          element={
            <ProtectedRoute >
              <Layout>
                <BootModelFormWrapper />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Boten Routes */}
        <Route
          path="/boten"
          element={
            <ProtectedRoute requiredPermission="boten_bekijken">
              <Layout>
                <BotenList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/boten/new"
          element={
            <ProtectedRoute requiredPermission="boten_bekijken">
              <Layout>
                <BotenForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/boten/:id"
          element={
            <ProtectedRoute >
              <Layout>
                <BotenFormWrapper />
              </Layout>
            </ProtectedRoute>
          }
        />


        {/* Settings Routes */}
        <Route
          path="/settings/bedrijfsgegevens"
          element={
            <ProtectedRoute requiredPermission="instellingen_bekijken">
              <Layout>
                <Bedrijfsgegevens />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/email"
          element={
            <ProtectedRoute requiredPermission="instellingen_bekijken">
              <Layout>
                <EmailInstellingen />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/algemeen"
          element={
            <ProtectedRoute requiredPermission="instellingen_bekijken">
              <Layout>
                <AlgemeneInstellingen />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/rollen"
          element={
            <ProtectedRoute requiredPermission="rollen_beheren">
              <Layout>
                <RollenPermissies />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/users"
          element={
            <ProtectedRoute requiredPermission="gebruikers_beheren">
              <Layout>
                <UsersList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/users/new"
          element={
            <ProtectedRoute requiredPermission="gebruikers_beheren">
              <Layout>
                <UsersForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/users/:userId"
          element={
            <ProtectedRoute requiredPermission="gebruikers_beheren">
              <Layout>
                <UsersForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/database-export"
          element={
            <ProtectedRoute requiredPermission="instellingen_bekijken">
              <Layout>
                <DatabaseExport />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/todo"
          element={
            <ProtectedRoute requiredPermission="instellingen_bekijken">
              <Layout>
                <TodoList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/help"
          element={
            <ProtectedRoute >
              <Layout>
                <Help />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Motor Onderdelen View - voor specifieke motor */}
        <Route
          path="/motor-onderdelen/:motorId"
          element={
            <ProtectedRoute >
              <Layout>
                <MotorOnderdelenView />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Onderdelen Routes */}
        <Route
          path="/onderdelen"
          element={
            <ProtectedRoute>
              <Layout>
                <OnderdelenList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/onderdelen/new"
          element={
            <ProtectedRoute>
              <Layout>
                <OnderdeelForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/onderdelen/:onderdeelId"
          element={
            <ProtectedRoute>
              <Layout>
                <OnderdeelForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/onderdelen/:onderdeelId/documenten"
          element={
            <ProtectedRoute>
              <Layout>
                <OnderdeelDocumentenList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/onderdelen/:onderdeelId/documenten/new"
          element={
            <ProtectedRoute>
              <Layout>
                <OnderdeelDocumentForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/onderdelen/:onderdeelId/documenten/:documentId"
          element={
            <ProtectedRoute>
              <Layout>
                <OnderdeelDocumentForm />
              </Layout>
            </ProtectedRoute>
          }
        />



        {/* Voorraad Routes */}
        <Route
          path="/voorraad"
          element={
            <ProtectedRoute requiredPermission="magazijn_bekijken">
              <Layout>
                <VoorraadList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/voorraad/groep/:groepId"
          element={
            <ProtectedRoute requiredPermission="magazijn_bekijken">
              <Layout>
                <VoorraadGroepDetail />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/voorraad/nieuwe-subgroep"
          element={
            <ProtectedRoute requiredPermission="magazijn_bekijken">
              <Layout>
                <SubgroepForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/voorraad/item/:itemId/onderdelen"
          element={
            <ProtectedRoute requiredPermission="magazijn_bekijken">
              <Layout>
                <ItemOnderdelenBeheer />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Offertes Routes */}
        <Route
          path="/offertes"
          element={
            <ProtectedRoute requiredPermission="offertes_bekijken">
              <Layout>
                <OffertesList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/offertes/new"
          element={
            <ProtectedRoute requiredPermission="offertes_aanmaken">
              <Layout>
                <OfferteForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/offertes/:id"
          element={
            <ProtectedRoute requiredPermission="offertes_bekijken">
              <Layout>
                <OfferteDetailWrapper />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/offertes/:id/edit"
          element={
            <ProtectedRoute requiredPermission="offertes_wijzigen">
              <Layout>
                <OfferteFormWrapper />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Werkorders Routes */}
        <Route
          path="/werkorders"
          element={
            <ProtectedRoute>
              <Layout>
                <WerkordersList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/werkorders/new"
          element={
            <ProtectedRoute>
              <Layout>
                <WerkorderForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/werkorders/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <WerkorderDetailWrapper />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/werkorders/:id/edit"
          element={
            <ProtectedRoute>
              <Layout>
                <WerkorderFormWrapper />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Urenregistratie */}
        <Route
          path="/urenregistratie"
          element={
            <ProtectedRoute requiredPermission="uren_bekijken">
              <Layout>
                <UrenRegistratie />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Kilometerregistratie */}
        <Route
          path="/kilometerregistratie"
          element={
            <ProtectedRoute>
              <Layout>
                <KilometerRegistratie />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Bestelpool Routes */}
        <Route
          path="/bestelpool"
          element={
            <ProtectedRoute >
              <Layout>
                <BestellingenList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/bestelpool/new"
          element={
            <ProtectedRoute >
              <Layout>
                <BestellingForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/bestelpool/:id"
          element={
            <ProtectedRoute >
              <Layout>
                <BestellingForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Bestellingen Nieuwe Route */}
        <Route
          path="/bestellingen/new"
          element={
            <ProtectedRoute >
              <Layout>
                <BestellingForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Bestellingen Detail Route */}
        <Route
          path="/bestellingen/:id"
          element={
            <ProtectedRoute >
              <Layout>
                <BestellingForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Bestellingen Historie Route */}
        <Route
          path="/bestellingen"
          element={
            <ProtectedRoute >
              <Layout>
                <BestellingenHistorie />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Facturen Routes */}
        <Route
          path="/facturen"
          element={
            <ProtectedRoute >
              <Layout>
                <FacturenList />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/facturen/new"
          element={
            <ProtectedRoute >
              <Layout>
                <FactuurForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/facturen/:id"
          element={
            <ProtectedRoute >
              <Layout>
                <FactuurDetail />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/facturen/:id/edit"
          element={
            <ProtectedRoute >
              <Layout>
                <FactuurForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Boekhouding Routes */}
        <Route
          path="/boekhouding"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <BoekingenList />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/dashboard"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <BoekhoudingDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/boekhouding/instellingen"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <BoekhoudingInstellingen />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/boeking/nieuw"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <BoekingForm />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/boeking/:id"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <BoekingDetail />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/boeking/:id/bewerk"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <BoekingForm />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/btw"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <BTWOverzicht />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/exploitatie"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <Exploitatie />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/balans"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <Balans />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/afschrijvingen"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <Afschrijvingen />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/beginbalans"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <BeginbalansPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/kapitaal"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <Kapitaal />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/help"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <BoekhoudingHelp />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/leningen"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <Leningen />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/dc"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <DebiteurenCrediteuren />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/jaarrekening"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <Jaarrekening />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/relaties"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <Relaties />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/relatie/nieuw"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <RelatieForm />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/relatie/:id/bewerk"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <RelatieForm />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boekhouding/scan"
          element={
            <ProtectedRoute requiredPermission="boekhouding_bekijken">
              <Layout>
                <FactuurScan />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

function App() {
  const basename = '/';
  
  // Clean up debug code
  
  // Simpele versie tracking (geen auto-reload)
  useEffect(() => {
    const currentVersion = 'v20261102_233206';
    const lastVersion = localStorage.getItem('buro42_version');

    // Alleen versie bijwerken, geen cache clearing
    if (!lastVersion || lastVersion !== currentVersion) {
      localStorage.setItem('buro42_version', currentVersion);
    }
  }, []);

  return (
    <Router basename={basename}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

// Wrappers to extract route params using useParams hook
const RelationDetailWrapper = () => {
  const { id } = useParams<{ id: string }>();
  return <RelationDetail relationId={parseInt(id || '0')} />;
};

const BootModelFormWrapper = () => {
  const { id } = useParams<{ id: string }>();
  return <BootModelForm bootModelId={parseInt(id || '0')} />;
};

const BotenFormWrapper = () => {
  const { id } = useParams<{ id: string }>();
  return <BotenForm bootId={parseInt(id || '0')} />;
};

const WerknemerFormWrapper = () => {
  const { id } = useParams<{ id: string }>();
  return <WerknemerForm werknemerId={parseInt(id || '0')} />;
};

const OfferteDetailWrapper = () => {
  const { id } = useParams<{ id: string }>();
  return <OfferteDetail offerteId={parseInt(id || '0')} />;
};

const OfferteFormWrapper = () => {
  const { id } = useParams<{ id: string }>();
  return <OfferteForm offerteId={parseInt(id || '0')} />;
};

const WerkorderDetailWrapper = () => {
  const { id } = useParams<{ id: string }>();
  return <WerkorderDetail werkorderId={parseInt(id || '0')} />;
};

const WerkorderFormWrapper = () => {
  const { id } = useParams<{ id: string }>();
  return <WerkorderForm werkorderId={parseInt(id || '0')} />;
};

export default App;
